<!-- src/components/CalendarMonth.vue -->
<template>
  <div :class="['flex flex-col', { 'px-1': isLargeScreen, 'px-0': !isLargeScreen }]">
    <div class="text-center font-semibold sm:mb-1 sm:text-base text-sm">
      {{ monthYear }}
    </div>
    <div :class="['grid grid-cols-7', { 'pb-1': isLargeScreen, 'pb-0': !isLargeScreen }]">
      <!-- Render days of the week -->
      <div
        v-for="day in daysOfWeek"
        :key="day"
        class="text-center sm:text-sm text-xs md:px-2 px-3 text-gray-600"
      >
        {{ day }}
      </div>
      <!-- Render days of the month -->
      <div
        v-for="(date, index) in monthDays"
        :key="date ? date.toString() : `empty-${index}`"
        :class="[
          getDateClasses(date),
          'text-center cursor-pointer relative border-2 border-transparent',
          { 'hover:bg-blue-50': !isInHoverRange(date) },
          isLargeScreen ? 'sm:text-sm text-sm sm:p-1.5 p-1' : 'text-sm p-1',
          disableDate(date) ? 'text-slate-300 cursor-not-allowed' : '',
          getWeatherClasses(date)
        ]"
        @click="!disableDate(date) && selectDateHandler(date)"
        @mouseenter="!disableDate(date) && date && emit('hoverDate', date)"
        @mouseleave="emit('hoverLeave')"
        role="button"
        tabindex="0"
        @keydown.enter.prevent="!disableDate(date) && selectDateHandler(date)"
        @keydown.space.prevent="!disableDate(date) && selectDateHandler(date)"
      >
        <span
          v-if="date && !disableDate(date) && getHoverLabel(date) && (isSelectedEndpoint(date) || (hoveredDate && normalizeDate(date) === normalizeDate(hoveredDate)))"
          class="absolute -top-3 left-1/2 -translate-x-1/2 text-[9px] font-semibold text-blue-500 bg-white border-t border-l border-r border-blue-300 rounded-t px-1.5 leading-normal whitespace-nowrap pointer-events-none z-10"
        >{{ getHoverLabel(date) }}</span>
        <span
          v-if="date && isForecastBoundary(date)"
          class="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[8px] font-semibold text-amber-600 bg-white border-b border-l border-r border-amber-300 rounded-b px-1 leading-normal whitespace-nowrap pointer-events-none z-10"
        >Forecast</span>
        {{ date ? date.getDate() : '' }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, tm, locale } = useI18n();

const props = defineProps({
  month: {
    type: Date,
    required: true,
  },
  selectedDates: {
    type: Object,
    required: true,
  },
  isLargeScreen: {
    type: Boolean,
    required: true,
  },
  bgColor: {
    type: String,
    default: 'bg-blue-500',
  },
  exactDays: { // Updated prop name
    type: Boolean,
    default: false,
  },
  hoveredDate: {
    type: Date,
    default: null,
  },
  isWeatherMode: {
    type: Boolean,
    default: false,
  },
  forecastEndDate: {
    type: String,
    default: null,
  },
  dateMode: {
    type: String,
    default: 'range',
  },
  tripType: {
    type: String,
    default: 'return',
  },
});

const emit = defineEmits(['selectDate', 'hoverDate', 'hoverLeave']);

const daysOfWeek = computed(() => tm('searchForm.weekdays'));

const monthYear = computed(() => {
  return props.month.toLocaleDateString(locale.value === 'pl' ? 'pl-PL' : 'en-GB', { month: 'long', year: 'numeric' });
});

const monthDays = computed(() => {
  const start = new Date(props.month.getFullYear(), props.month.getMonth(), 1);
  const end = new Date(props.month.getFullYear(), props.month.getMonth() + 1, 0);

  const dates = [];
  if (!isNaN(start.getTime())) {
    const dayOfWeek = (start.getDay() + 6) % 7; // Monday=0, Sunday=6
    for (let i = 0; i < dayOfWeek; i++) {
      dates.push(null); // Fill the grid with null to maintain alignment
    }
    for (let day = 1; day <= end.getDate(); day++) {
      const currentDate = new Date(props.month.getFullYear(), props.month.getMonth(), day);
      dates.push(currentDate);
    }
  }

  // Split into weeks (7-day chunks)
  const weeks = [];
  for (let i = 0; i < dates.length; i += 7) {
    weeks.push(dates.slice(i, i + 7));
  }

  // Filter out weeks where all dates are disabled (in the past)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 18, today.getDate());

  const filteredWeeks = weeks.filter(week => {
    const hasEnabledDate = week.some(date => {
      if (!date) return false;
      return date.getTime() >= today.getTime() && date.getTime() <= maxDate.getTime();
    });
    return hasEnabledDate;
  });

  // Flatten back to array and pad last week with nulls for grid alignment
  const filteredDates = filteredWeeks.flat();
  const remainder = filteredDates.length % 7;
  if (remainder !== 0) {
    for (let i = 0; i < 7 - remainder; i++) {
      filteredDates.push(null);
    }
  }

  return filteredDates;
});

const normalizeDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
};

const getDateClasses = (date) => {
  if (!date) return 'invisible';

  const start = props.selectedDates.start ? normalizeDate(props.selectedDates.start) : null;
  const end = props.selectedDates.end ? normalizeDate(props.selectedDates.end) : null;
  const current = normalizeDate(date);

  if (start && end) {
    if (start === end && current === start) {
      return 'calendar-start-indicator text-blue-600 font-semibold';
    } else if (current === start) {
      return props.exactDays ? 'calendar-start-indicator text-blue-600 font-semibold' : 'calendar-range-start text-blue-600 font-semibold';
    } else if (current === end) {
      return props.exactDays ? 'calendar-start-indicator text-blue-600 font-semibold' : 'calendar-range-end text-blue-600 font-semibold';
    } else if (!props.exactDays && current > start && current < end) {
      return 'bg-blue-50 text-blue-800';
    } else {
      return 'bg-white text-gray-900';
    }
  } else if (start) {
    const hovered = props.hoveredDate ? normalizeDate(props.hoveredDate) : null;
    if (current === start) {
      // Open the start indicator toward the hovered date
      if (hovered && hovered !== start) {
        return hovered > start
          ? 'calendar-range-start text-blue-600 font-semibold'
          : 'calendar-range-end text-blue-600 font-semibold';
      }
      return 'calendar-start-indicator text-blue-600 font-semibold';
    }
    // Hover preview range
    if (hovered && hovered !== start) {
      const rangeStart = Math.min(start, hovered);
      const rangeEnd = Math.max(start, hovered);
      if (current > rangeStart && current < rangeEnd) {
        return 'bg-blue-50 text-blue-800';
      }
      if (current === hovered) {
        return hovered > start
          ? 'calendar-range-end text-blue-600 font-semibold'
          : 'calendar-range-start text-blue-600 font-semibold';
      }
    }
    return 'bg-white text-gray-900';
  } else if (end) {
    // If only the end date is selected, highlight just the end date
    if (current === end) {
      return `${props.bgColor} text-white`;
    } else {
      return 'bg-white text-gray-900';
    }
  }

  return 'bg-white text-gray-900';
};

const selectDateHandler = (date) => {
  let newStart = props.selectedDates.start;
  let newEnd = props.selectedDates.end;
  
  // Create a date string in ISO format that won't be affected by timezone issues
  // Use noon (12:00) to avoid any timezone boundary issues
  const createDateString = (d) => {
    // Create a new date with the same year, month, and day, but at noon
    const newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
    return newDate.toISOString();
  };

  console.log('Selected date:', date);

  // Exact dates + one-way: single date selection (start = end = clicked)
  if (props.exactDays && props.tripType === 'one-way') {
    const ds = createDateString(date);
    newStart = ds;
    newEnd = ds;
  } else if (newStart && newEnd) {
    // Both dates are selected, reset to new start date
    newStart = createDateString(date);
    newEnd = null;
  } else if (!newStart || newEnd) {
    // No start date or end date is already selected, set new start date
    newStart = createDateString(date);
    newEnd = null;
  } else if (newStart && !newEnd) {
    // Start date is selected, set end date
    const startDate = new Date(newStart);
    if (date >= startDate) {
      newEnd = createDateString(date);
    } else {
      // If selected date is before start date, swap them
      newEnd = newStart;
      newStart = createDateString(date);
    }
  }

  console.log('Emitting dates:', { start: newStart, end: newEnd });
  // Emit the new start and end dates to the parent component
  emit('selectDate', { start: newStart, end: newEnd });
};

const isSelectedEndpoint = (date) => {
  const start = props.selectedDates.start ? normalizeDate(props.selectedDates.start) : null;
  const end = props.selectedDates.end ? normalizeDate(props.selectedDates.end) : null;
  if (!start || !end) return false;
  const current = normalizeDate(date);
  return current === start || current === end;
};

const getHoverLabel = (date) => {
  const start = props.selectedDates.start ? normalizeDate(props.selectedDates.start) : null;
  const end = props.selectedDates.end ? normalizeDate(props.selectedDates.end) : null;
  const current = normalizeDate(date);
  const isExact = props.dateMode === 'exact';
  const isOneWay = props.tripType === 'one-way';
  if (start && end) {
    if (start === end && current === start) return isExact ? (isOneWay ? t('searchForm.calFlyOn') : t('searchForm.calDepart')) : t('searchForm.calExact');
    if (current === start) return isExact ? t('searchForm.calDepart') : t('searchForm.calAfter');
    if (current === end) return isExact ? t('searchForm.calReturn') : t('searchForm.calBefore');
    return null;
  }
  if (!start) return isExact ? (isOneWay ? t('searchForm.calFlyOn') : t('searchForm.calDepart')) : t('searchForm.calRange');
  if (current === start) return null;
  return isExact ? (isOneWay ? t('searchForm.calFlyOn') : t('searchForm.calReturn')) : t('searchForm.calRange');
};

const forecastLastDay = computed(() => {
  if (props.forecastEndDate) {
    const d = new Date(`${props.forecastEndDate}T00:00:00`);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 16);
  return d.getTime();
});

const getWeatherClasses = (date) => {
  if (!props.isWeatherMode || !date || disableDate(date)) return '';
  const current = normalizeDate(date);
  const start = props.selectedDates.start ? normalizeDate(props.selectedDates.start) : null;
  const end = props.selectedDates.end ? normalizeDate(props.selectedDates.end) : null;
  const inSelectedRange = start && end && current >= start && current <= end;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (current >= today.getTime() && current <= forecastLastDay.value) {
    if (current === forecastLastDay.value) {
      return inSelectedRange ? 'border-r-amber-400' : 'bg-amber-50/60 border-r-amber-400';
    }
    return inSelectedRange ? '' : 'bg-amber-50/60';
  }
  return '';
};

const isForecastBoundary = (date) => {
  if (!props.isWeatherMode || !date) return false;
  return normalizeDate(date) === forecastLastDay.value;
};

const isInHoverRange = (date) => {
  if (!date || !props.hoveredDate) return false;
  const start = props.selectedDates.start ? normalizeDate(props.selectedDates.start) : null;
  const end = props.selectedDates.end ? normalizeDate(props.selectedDates.end) : null;
  if (!start || end) return false;

  const current = normalizeDate(date);
  const hovered = normalizeDate(props.hoveredDate);
  const rangeStart = Math.min(start, hovered);
  const rangeEnd = Math.max(start, hovered);
  return current >= rangeStart && current <= rangeEnd;
};

const disableDate = (date) => {
  if (!date) return true; // Return true to disable if date is null or undefined

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize the time to midnight to compare just the dates

  // Calculate the date 18 months from today
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 18, today.getDate());

  // Disable dates before today and after 18 months from today
  return date.getTime() < today.getTime() || date.getTime() > maxDate.getTime();
};
</script>

<style scoped>
.calendar-start-indicator {
  background: #dbeafe;
  border-radius: 6px;
  border-color: #93c5fd;
  animation: start-pulse 2s ease-in-out infinite;
  z-index: 1;
}
@keyframes start-pulse {
  0%, 100% { box-shadow: 0 0 8px 0 rgba(59,130,246,0.2); border-color: #93c5fd; }
  50% { box-shadow: 0 0 12px 2px rgba(59,130,246,0.35); border-color: #60a5fa; }
}
/* Range start: open on the right side */
.calendar-range-start {
  background: #dbeafe;
  border-radius: 6px 0 0 6px;
  border-color: #93c5fd;
  border-right-color: transparent;
  z-index: 1;
}
/* Range end: open on the left side */
.calendar-range-end {
  background: #dbeafe;
  border-radius: 0 6px 6px 0;
  border-color: #93c5fd;
  border-left-color: transparent;
  z-index: 1;
}
</style>
